import { useEffect, useState } from 'react';
import { upsertVisitorDataProcedure } from '@krishanmadan/core/src/procedures/upsertVisitorDataProcedure';
import { useLatestRef } from '@epihub/web/src/hooks/useLatestRef';
import { useDebouncedCallback } from '@epihub/web/src/hooks/useDebouncedCallback';
import type { SupportedLanguage } from '@krishanmadan/core/src/model/const';
import { useGlobalStore } from '../store/useGlobalStore';
import { getVisitorId } from '../lib/fingerprint';
import { useMutationProcedure } from '../_ctx/queryClient';
import { Analytics } from '../lib/Analytics';
import { updateLanguage } from '../lib/updateLanguage';

export function useInitializeVisitor() {
  const visitorId = useGlobalStore((store) => store.settings.visitorId);

  useEffect(() => {
    if (visitorId == null) {
      (async () => {
        const visitorId = await getVisitorId();
        useGlobalStore.getState().settings.__setVisitorId(visitorId);
      })();
    }
  }, [visitorId]);

  return useUpsertVisitorData(visitorId);
}

export function useUpsertVisitorData(visitorId: string | null) {
  const visitorName = useGlobalStore((store) => store.settings.visitorName);
  const language = useGlobalStore((store) => store.settings.language);
  const soundOn = useGlobalStore((store) => store.settings.soundOn);
  const quickResponses = useGlobalStore((store) => store.settings.quickResponses);
  const misuseRate = useGlobalStore((store) => store.settings.misuseRate);
  const conversation = useGlobalStore((store) => store.conversation.conversation);

  const dataRef = useLatestRef({
    visitorId,
    visitorName,
    language,
    soundOn,
    quickResponses,
    misuseRate,
    conversation,
  });

  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);
  const hasInitiallyLoadedRef = useLatestRef(hasInitiallyLoaded);

  const upsertVisitorData = useMutationProcedure(upsertVisitorDataProcedure);

  const sync = useDebouncedCallback(async () => {
    const visitorId = dataRef.current.visitorId;
    if (!visitorId) {
      return;
    }

    const result = await upsertVisitorData.mutateAsync({
      request: {
        params: { visitorId },
        query: { isInitialLoad: !hasInitiallyLoadedRef.current },
        body: {
          ...dataRef.current,
          visitorId,
        },
      },
    });

    // Only update the UI with the backend response on first load or else it's very
    // likely that request (N-1)'s response will overwrite request N's request data
    if (hasInitiallyLoadedRef.current) {
      return;
    }

    Analytics.identify({
      visitorId,
      name: result.visitorName,
      language: result.language as SupportedLanguage,
    });

    setHasInitiallyLoaded(true);
    useGlobalStore.setState((prev) => ({
      ...prev,
      settings: {
        ...prev.settings,
        visitorName: result.visitorName,
        soundOn: result.soundOn,
        quickResponses: result.quickResponses,
        misuseRate: result.misuseRate,
        // The language must be updated through 'updateLanguage' helper
        // language: result.language,
      },
      conversation: {
        ...prev.conversation,
        conversation: result.conversation,
      },
    }));
    updateLanguage(result.language);
  }, 500);

  useEffect(() => {
    if (visitorId) {
      sync();
    }
  }, [
    visitorId,
    visitorName,
    language,
    soundOn,
    quickResponses,
    conversation,
  ]);

  return { hasInitiallyLoaded, upsertVisitorData };
}
